import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PhoneOff } from "lucide-react";

const COLUMNS = [
  { key: "new", label: "New Missed", color: "border-sky-500/40", head: "text-sky-300" },
  { key: "contacted", label: "SMS Contacted", color: "border-orange-500/40", head: "text-orange-300" },
  { key: "qualified", label: "Roof Qualified", color: "border-amber-500/40", head: "text-amber-300" },
  { key: "booked", label: "Estimate Booked", color: "border-emerald-500/40", head: "text-emerald-300" },
  { key: "won", label: "Contract Won", color: "border-emerald-500/70", head: "text-emerald-200" },
];

export default function Pipeline() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  // Suppress click after a drag so dropping a card doesn't also navigate.
  const [justDragged, setJustDragged] = useState(false);

  const load = () => api.get("/leads").then(r => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const drop = async (status) => {
    if (!dragId) return;
    const lead = leads.find(l => l.id === dragId);
    if (!lead || lead.status === status) { setDragId(null); return; }
    setLeads(prev => prev.map(l => l.id === dragId ? { ...l, status } : l));
    setDragId(null);
    try {
      await api.patch(`/leads/${dragId}/status`, { status });
      toast.success(`Moved to ${status}`);
    } catch {
      toast.error("Failed to update");
      load();
    }
  };

  const total = leads.reduce((s, l) => s + (["booked","won"].includes(l.status) ? l.estimated_value : 0), 0);

  return (
    <div className="p-6 lg:p-10 max-w-[1800px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">Pipeline</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Lead Pipeline</h1>
          <p className="text-sm text-slate-400 mt-1">Drag cards to move leads through the pipeline</p>
        </div>
        <Card className="bg-slate-900 border-slate-800 px-5 py-3">
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Pipeline Value</p>
          <p className="font-display text-2xl font-extrabold text-emerald-400 font-mono">${total.toLocaleString()}</p>
        </Card>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {COLUMNS.map((col) => {
          const items = leads.filter(l => l.status === col.key);
          const sum = items.reduce((s, l) => s + l.estimated_value, 0);
          return (
            <div key={col.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => drop(col.key)}
              data-testid={`col-${col.key}`}
              className={`bg-slate-900/60 border ${col.color} rounded-xl p-3 min-h-[500px] flex flex-col`}>
              <div className="mb-3 px-1">
                <div className="flex items-center justify-between">
                  <h3 className={`font-display text-sm font-bold uppercase tracking-wider ${col.head}`}>{col.label}</h3>
                  <Badge className="bg-slate-800 text-slate-300 border-slate-700">{items.length}</Badge>
                </div>
                <p className="text-xs text-slate-500 font-mono mt-1">${sum.toLocaleString()}</p>
              </div>
              <div className="space-y-2 flex-1 overflow-y-auto">
                {items.map((l) => (
                  <div key={l.id}
                    draggable
                    onDragStart={() => { setDragId(l.id); setJustDragged(true); }}
                    onDragEnd={() => { setDragId(null); setTimeout(() => setJustDragged(false), 150); }}
                    onClick={() => { if (!justDragged) navigate(`/app/chat/${l.id}`); }}
                    role="button"
                    tabIndex={0}
                    data-testid={`pipe-card-${l.id}`}
                    title="Click to open conversation · drag to move stage"
                    className="bg-slate-900 border border-slate-800 rounded-lg p-3 cursor-pointer hover:border-orange-500/40 transition-colors active:cursor-grabbing">
                    <div className="flex items-start gap-2 mb-2">
                      <div className="w-7 h-7 rounded bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                        <PhoneOff className="w-3 h-3 text-orange-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{l.caller_name}</p>
                        <p className="text-xs text-slate-500 font-mono truncate">{l.caller_phone}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-300 mb-2 line-clamp-2">{l.issue}</p>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                      <span className="text-xs text-slate-500 truncate">{l.roof_type}</span>
                      <span className="font-mono text-xs font-bold text-emerald-400">${(l.estimated_value/1000).toFixed(1)}k</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
